import { MODES, MODE_KEYS, type Mode } from '../game/engine';
import { fetchBoard, type Board } from '../net/api';
import type { Player } from '../player';
import { el } from './dom';

/** the leaderboard page: one board per ranked mode, best round per player, highest first */
export function renderBoard(root: HTMLElement, player: Player, nav: { back(): void; play(): void }): () => void {
  const ranked = MODE_KEYS.filter((m) => MODES[m].game);
  let mode: Mode = MODES[player.mode].game ? player.mode : 'sloth';
  const list = el('ol', { class: 'board-list' });
  const status = el('p', { class: 'dim center' }, 'Loading…');
  const tabs = el(
    'div',
    { class: 'tabs' },
    ...ranked.map((m) => {
      const b = el('button', { class: 'tab', type: 'button' }, MODES[m].label) as HTMLButtonElement;
      b.onclick = () => {
        mode = m;
        paintTabs();
        void load();
      };
      return b;
    }),
  );
  const paintTabs = (): void => {
    [...tabs.children].forEach((b, i) => b.classList.toggle('on', ranked[i] === mode));
  };
  paintTabs();
  const wrap = el(
    'section',
    { class: 'page board' },
    el('header', { class: 'page-top' }, el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'back', onclick: nav.back }, '‹'), el('h1', {}, 'Leaderboard'), el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'refresh', onclick: () => void load() }, '↻')),
    tabs,
    el('p', { class: 'dim center' }, 'best round per player'),
    status,
    list,
    el('div', { class: 'spacer' }),
    el('button', { class: 'btn primary big', type: 'button', onclick: nav.play }, 'Play'),
  );
  root.append(wrap);

  function row(rank: number, name: string, score: number, detail: Board['entries'][number]['detail'], at: number, me: boolean): HTMLElement {
    const when = new Date(at * 1000);
    const bits = [];
    if (detail?.hits !== undefined) bits.push(`${detail.hits} 🥥`);
    if (detail?.farthest !== undefined) bits.push(`${Math.round(detail.farthest)} m`);
    bits.push(when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
    return el(
      'li',
      { class: 'board-row' + (me ? ' me' : '') },
      el('span', { class: 'rank' }, rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : `${rank}`),
      el('span', { class: 'name' }, name, me ? el('small', {}, ' (you)') : null),
      el('span', { class: 'score' }, String(score)),
      el('small', { class: 'meta' }, bits.join(' · ')),
    );
  }

  async function load(): Promise<void> {
    status.textContent = 'Loading…';
    status.hidden = false;
    list.replaceChildren();
    const board = await fetchBoard(player.id, 25, MODES[mode].game!);
    if (!board) {
      status.textContent = 'Could not reach the leaderboard. Are you online?';
      return;
    }
    if (!board.entries.length) {
      status.textContent = 'No one yet. Be the first!';
      return;
    }
    status.hidden = true;
    board.entries.forEach((e, i) => list.append(row(i + 1, e.name, e.score, e.detail, e.at, e.player === player.id)));
    if (board.me && board.me.rank > board.entries.length) {
      list.append(el('li', { class: 'board-gap' }, '…'));
      list.append(row(board.me.rank, player.name || 'you', board.me.best, null, Date.now() / 1000, true));
    }
  }
  void load();
  return () => wrap.remove();
}
